import { ChangeDetectorRef, Component, EventEmitter, inject, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, NgForm } from '@angular/forms';
import * as netmask from 'netmask';

import { ApiProvitoolService } from '@core/apis/api-provitool.service';
import { IP_PATTERN, IP_WITH_MASK_PATTERN } from '@core/constants';
import { GenericListComponent } from '@core/globals/generic-list/generic-list.component';
import { WcmModalsService } from '@core/globals/wcm-modals/wcm-modals.service';
import { WcmTableComponent } from '@core/globals/wcm-table/wcm-table.component';
import { isString, omit } from '@core/helpers';
import {
  CATO_SUBNET_DHCP_TYPE_OPTIONS,
  CATO_SUBNET_INTERFACE_OPTIONS,
  CATO_SUBNET_RANGE_TYPE_OPTIONS,
  CatoSubnetDhcpTypeEnum,
  CatoSubnetRangeTypeEnum,
  ICatoSubnet,
  IGenericFilters,
  IListResult,
  INetworkDevice,
} from '@core/interfaces';
import { ObjectToolService } from '@core/services/object-tool.service';
import { SignalsService } from '@core/services/signals.service';
import { WaycomHttpErrorResponse } from '@core/services/waycom-http-error-response';

interface IEditableCatoSubnet extends ICatoSubnet {
  editable?: boolean;
  backup?: ICatoSubnet;
  invalidVlan?: boolean;
  range?: string;
}

@Component({
  selector: 'app-network-device-cato-subnet-list',
  templateUrl: './network-device-cato-subnet-list.component.html',
})
export class NetworkDeviceCatoSubnetListComponent extends GenericListComponent implements OnInit {
  private readonly objectToolService = inject(ObjectToolService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  public readonly apiProvitool = inject(ApiProvitoolService);
  private readonly signalsService = inject(SignalsService);
  private readonly wcmModalsService = inject(WcmModalsService);

  @ViewChild('wcmTable', { static: true }) public wcmTable: WcmTableComponent;
  @ViewChild('f', { static: true }) public detailForm: NgForm;

  @Input() public networkDevice: INetworkDevice;
  @Output() public createOrDelete: EventEmitter<void> = new EventEmitter();

  public readonly CatoSubnetRangeTypeEnum = CatoSubnetRangeTypeEnum;
  public readonly CatoSubnetDhcpTypeEnum = CatoSubnetDhcpTypeEnum;
  public readonly ipPattern: RegExp = IP_PATTERN;
  public readonly ipWithMaskPattern: RegExp = IP_WITH_MASK_PATTERN;
  public readonly rangeTypeOptions = CATO_SUBNET_RANGE_TYPE_OPTIONS;
  public readonly dhcpTypeOptions = CATO_SUBNET_DHCP_TYPE_OPTIONS;
  public readonly interfaceOptions = CATO_SUBNET_INTERFACE_OPTIONS;

  public editionInProgress: boolean;
  public loadingItemAction: boolean;

  public readonly catoSubnetApi = {
    list: (filters: IGenericFilters): Promise<IListResult<IEditableCatoSubnet>> => this.apiProvitool.catoSubnets.list(filters).then(
      (response: IListResult<ICatoSubnet>) => {
        response.results = response.results.map((subnet: ICatoSubnet): IEditableCatoSubnet => {
          return {
            ...subnet,
            range: `${subnet.range_start}-${subnet.range_end}`,
          };
        });
        return response;
      },
    ),
  };

  constructor(
    public injector: Injector,
  ) {
    super(injector);
  }

  public ngOnInit(): void {
    this.localFilters = {
      network_device__code: this.networkDevice.code
    };
  }

  public addCatoSubnet(): void {
    if (this.editionInProgress) {
      return;
    }

    const emptyRow: IEditableCatoSubnet = {
      editable: true,
      id: null,
      address: null,
      dhcp_type: null,
      gateway_address: null,
      interface: 'LAN1',
      internet_only: false,
      name: null,
      range_end: null,
      range_start: null,
      range_type: null,
      relay_group: null,
      subnet: null,
      vlan: null,
      network_device: this.networkDevice,
    };
    this.wcmTable.items.unshift(emptyRow);
    this.editionInProgress = true;
  }

  public dhcpTypeChanged(): void {
    this.changeDetectorRef.detectChanges();
  }

  public checkVlanNumber(item: IEditableCatoSubnet): void {
    item.invalidVlan = item.vlan && (item.vlan < 0 || item.vlan > 4096);
  }

  public fetchCallback(): void {
    this.editionInProgress = false;
  }

  public checkNetworkContainsIp(item: IEditableCatoSubnet): void {
    const controlNameSuffix: string = item.id ? `${item.id}` : '';

    const networkControl: AbstractControl | undefined = this.detailForm.controls[`subnet_${controlNameSuffix}`];
    const addressControl: AbstractControl | undefined = this.detailForm.controls[`address_${controlNameSuffix}`];
    const gatewayAddressControl: AbstractControl | undefined = this.detailForm.controls[`gateway_address_${controlNameSuffix}`];

    // Remove the error from all three controls (just in case the range was changed and angular thinks there is an error)
    this._removeControlError(networkControl, 'noNetworkAndIpOverlap');
    this._removeControlError(addressControl, 'noNetworkAndIpOverlap');
    this._removeControlError(gatewayAddressControl, 'noNetworkAndIpOverlap');

    // Depending on the range_type value, the IP is either in the address field or in the gateway_address field
    const ipField: string = item.range_type === CatoSubnetRangeTypeEnum.Routed ? 'gateway_address' : 'address';
    const ipControl: FormControl = this.detailForm.controls[`${ipField}_${controlNameSuffix}`] as FormControl;

    // Check the controls for errors, if there are then don't even bother comparing the values
    if (!networkControl.valid || !ipControl.valid) {
      return;
    }

    // If the IP field value isn't contained in the network, add the error
    const network = new netmask.Netmask(networkControl.value);
    if (!network.contains(ipControl.value)) {
      networkControl.setErrors({ noNetworkAndIpOverlap: true });
      ipControl.setErrors({ noNetworkAndIpOverlap: true });
    }
  }

  public edit(item: IEditableCatoSubnet): void {
    item.backup = { ...item };
    item.editable = true;
    this.editionInProgress = true;
  }

  public save(item: IEditableCatoSubnet): void {
    this.loadingItemAction = true;

    const payload = omit(item, 'editable', 'backup', 'invalidVlan', 'range');

    // Extract IP range start and end
    if (item.dhcp_type === CatoSubnetDhcpTypeEnum.Range && item.range) {
      const dashIndex: number = item.range.indexOf('-');
      payload.range_start = item.range.slice(0, dashIndex).trim();
      payload.range_end = item.range.slice(dashIndex + 1).trim();
    }

    let promise: Promise<ICatoSubnet>;
    if (item.id) {
      promise = this.apiProvitool.catoSubnets.update(payload.id, payload);
    } else {
      promise = this.apiProvitool.catoSubnets.create(payload);
    }

    promise
      .then(() => {
        this.wcmTable.refreshTable();
        this.editionInProgress = false;
        this.createOrDelete.emit();
        this.signalsService.broadcast('cato-subnet-list-refresh');
        this.signalsService.broadcast('model-history-list-refresh');
      })
      .catch((error: unknown) => this._handleError(error))
      .finally(() => this.loadingItemAction = false);
  }

  public cancelEdit(item: IEditableCatoSubnet): void {
    // If the item has no id, it's from a creation, we just remove it from the list
    if (!item.backup) {
      const itemIndex = this.wcmTable.items.indexOf(item);
      this.wcmTable.items.splice(itemIndex, 1);
    } else {
      // Otherwise, restoring the backup to cancel the edition and bring back the previous values.
      // We do a copy of the backup key before calling the replace function because the first step
      // in this function is to clear the given first object
      const backup = { ...item.backup };
      this.objectToolService.replaceObjContent(item, backup);
    }
    this.editionInProgress = false;
  }

  public confirmDelete(item: IEditableCatoSubnet): void {
    this.wcmModalsService
      .confirm(
        `Suppression d'un subnet Cato`,
        `Confirmez-vous la suppresion du subnet ?`,
        'Confirmer',
        'Annuler',
      )
      .then(() => this._delete(item))
      .catch(() => {});
  }

  private _handleError(error: unknown): void {
    if (error instanceof WaycomHttpErrorResponse) {
      const errorMap: Record<string, string> = {
        ADDRESS_REQUIRED: 'Adresse IP obligatoire.',
        GATEWAY_ADDRESS_REQUIRED: 'Gateway obligatoire.',
        RELAY_GROUP_REQUIRED: 'Relay Groupe obligatoire.',
        RANGE_START_REQUIRED: 'Le début de la plage est obligatoire.',
        RANGE_END_REQUIRED: 'La fin de la plage est obligatoire.',
        RANGE_END_CANNOT_BE_LESS_THAN_RANGE_START: 'La fin de la plage IP ne peut pas être avant le début.',
        NATIVE_SUBNET_ALREADY_EXIST: 'Un subnet natif existe déjà pour ce ND.',
        IP_RANGE_NOT_COHERENT_WITH_SUBNET: `La plage IP ne correspond pas au subnet.`,
        DUPLICATE: `Le nom doit être unique.`,
      };

      if (isString(error.detail)) {
        // Try and get generic error messages
        const errorMessage: string = errorMap[error.getFirstErrorMessage()];
        if (errorMessage) {
          this.toastr.error(errorMessage);
          return;
        }
      }

      // Try and get field specific keys
      const errorCodes: string[] = Object.values(error.detail);
      if (errorCodes.length) {
        const message: string = errorCodes.map((code: string) => errorMap[code] || code).join('<br />');
        this.toastr.error(message, '', { enableHtml: true });
        return;
      }
    }
    Promise.reject(error);
  }

  private _delete(item: ICatoSubnet): void {
    this.loadingItemAction = true;

    this.apiProvitool.catoSubnets.delete(item.id)
      .then(() => {
        this.wcmTable.refreshTable();
        this.createOrDelete.emit();
        this.signalsService.broadcast('cato-subnet-list-refresh');
        this.signalsService.broadcast('model-history-list-refresh');
      })
      .catch((error: unknown) => Promise.reject(error))
      .finally(() => this.loadingItemAction = false);
  }

  /**
   * Remove the provided errorCode from a control's errors
   */
  private _removeControlError(control: AbstractControl | undefined, errorCode: string): void {
    if (control?.errors && control?.errors[errorCode]) {
      delete control.errors[errorCode];
      // If that was the last error, we set the errors to null instead of leaving the empty object so Angular understands it is valid
      if (Object.keys(control.errors).length === 0) {
        control.setErrors(null);
      }
    }
  }
}
